import { useEffect, useRef, useState, createContext, useContext } from "react";
import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  ArrowRight,
  Loader2,
  Check,
  CheckCircle2,
  ShieldCheck,
  Users,
  Skull,
  Target,
  BatteryLow,
  RotateCcw,
  Menu,
  X,
  Lock,
  Quote,
  Sparkles,
} from "lucide-react";
import {
  initiateCheckout,
  subscribeLead,
  abImpression,
  createPaypalOrder,
  capturePaypalOrder,
  getPaypalConfig,
  persistUtm,
  LOGO_DARK_URL,
} from "@/lib/checkout";
import { trackEvent, trackLead, trackBeginCheckout, trackHomepageView, trackPricingView, initCtaTracking } from "@/lib/analytics";
import { SiteFooter } from "@/components/SiteFooter";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";

const SUPPORT_EMAIL = "support@commandcenteradhd.com";
const COVER_URL = `${process.env.REACT_APP_BACKEND_URL}/api/files/planner-cover.png`;
const PRICE = 17;

const NAVY = "#0B132B";
const TEAL = "#15816B";

const CheckoutContext = createContext(() => {});
const FreeFocusContext = createContext(() => {});

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

/* ============ HEADER ============ */
const LaunchBanner = () => {
  const openCheckout = useContext(CheckoutContext);
  return (
    <button
      type="button"
      onClick={openCheckout}
      data-testid="launch-banner"
      className="group flex w-full items-center justify-center gap-x-2 gap-y-0.5 bg-[#0B132B] px-4 py-2 text-center text-xs font-medium leading-tight text-white transition-colors duration-200 hover:bg-[#0F1B3A] sm:text-sm"
    >
      <Sparkles className="hidden h-4 w-4 flex-shrink-0 text-[#F5A623] sm:inline" aria-hidden="true" />
      <span>
        Launch offer: <span className="font-bold text-[#F5A623]">$17</span> for the Command Center. One time purchase, future updates included. No subscription.
      </span>
      <span className="font-bold text-[#5EEAD4] underline-offset-2 group-hover:underline">Get it</span>
    </button>
  );
};

const Header = () => {
  const openCheckout = useContext(CheckoutContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const [active, setActive] = useState("");
  const nav = [
    { label: "Why planners stop working", id: "autopsy" },
    { label: "Pricing", id: "pricing" },
    { label: "FAQ", id: "faq" },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );
    nav.forEach((n) => {
      const el = document.getElementById(n.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const go = (id) => {
    setMenuOpen(false);
    setActive(id);
    try { window.history.replaceState(null, "", `#${id}`); } catch (e) { /* no-op */ }
    scrollTo(id);
  };
  return (
    <header className="sticky top-0 z-50 border-b border-[#E6E9EE] bg-white/90 backdrop-blur-md" data-testid="site-header">
      <div className="mx-auto flex max-w-[1080px] items-center justify-between px-6 py-3">
        <Link to="/" data-testid="brand-logo" className="inline-flex items-center">
          <img src={LOGO_DARK_URL} alt="CommandCenterADHD" width="1085" height="537" className="h-9 w-auto object-contain sm:h-10" />
        </Link>
        <nav className="hidden items-center gap-8 md:flex" data-testid="desktop-nav">
          {nav.map((n) => (
            <button
              key={n.id}
              type="button"
              onClick={() => go(n.id)}
              data-testid={`nav-${n.id}`}
              data-active={active === n.id}
              className={`relative text-sm font-medium transition-colors hover:text-[#0B132B] ${
                active === n.id ? "text-[#0B132B]" : "text-[#5A6472]"
              }`}
            >
              {n.label}
              <span
                className={`absolute -bottom-1 left-0 h-0.5 rounded-full bg-[#15816B] transition-all duration-300 ${
                  active === n.id ? "w-full" : "w-0"
                }`}
              />
            </button>
          ))}
          <a href="/community" data-testid="nav-community" className="text-sm font-medium text-[#5A6472] transition-colors hover:text-[#0B132B]">Community</a>
        </nav>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={openCheckout}
            data-testid="header-cta-button"
            data-cta-location="nav"
            className="rounded-xl bg-[#0B132B] px-4 py-2.5 font-heading text-xs font-bold text-white transition-transform duration-200 hover:-translate-y-0.5 sm:text-sm"
          >
            Get the Command Center for ${PRICE}
          </button>
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-[#0B132B] hover:bg-[#F0F2F5] md:hidden"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {menuOpen && (
        <div className="border-t border-[#E6E9EE] bg-white px-6 py-2 md:hidden" data-testid="mobile-menu">
          <nav className="flex flex-col">
            {nav.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => go(n.id)}
                data-testid={`mobile-nav-${n.id}`}
                data-active={active === n.id}
                className={`flex items-center justify-between border-b border-[#F0F2F5] py-3 text-left text-sm font-medium last:border-b-0 ${
                  active === n.id ? "text-[#15816B]" : "text-[#5A6472] hover:text-[#0B132B]"
                }`}
              >
                {n.label}
                {active === n.id && <span className="h-2 w-2 rounded-full bg-[#15816B]" />}
              </button>
            ))}
            <a href="/community" data-testid="mobile-nav-community" className="border-b border-[#F0F2F5] py-3 text-left text-sm font-medium text-[#5A6472] last:border-b-0 hover:text-[#0B132B]">Community</a>
          </nav>
        </div>
      )}
    </header>
  );
};

/* ============ FREE PLANNER CAPTURE ============ */
const FreeCapture = ({ inputRef, testidPrefix = "lead", variant, source }) => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | done | error
  const [msg, setMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!valid) {
      setStatus("error");
      setMsg("Please enter a valid email address.");
      return;
    }
    setStatus("loading");
    setMsg("");
    try {
      const res = await subscribeLead(email, { variant, source: source || testidPrefix });
      setStatus("done");
      trackLead({ source: source || testidPrefix, variant });
      try { localStorage.setItem("cc_lead_captured", "1"); } catch (e) { /* no-op */ }
      if (res.download_url) window.open(res.download_url, "_blank", "noopener");
    } catch (err) {
      setStatus("error");
      setMsg("Something went wrong. Please try again.");
    }
  };

  if (status === "done") {
    return (
      <div
        data-testid={`${testidPrefix}-success`}
        className="mx-auto flex max-w-[520px] items-start gap-3 rounded-2xl border border-[#15816B]/30 bg-[#15816B]/8 px-5 py-4 text-left"
      >
        <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#15816B]" />
        <p className="text-[15px] leading-relaxed text-[#0B132B]">
          Check your inbox. Your planner is on its way, and it just opened in a new tab so you can start now.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="mx-auto w-full max-w-[520px]" data-testid={`${testidPrefix}-form`}>
      <div className="flex flex-col gap-3 sm:flex-row">
        <label htmlFor={`${testidPrefix}-email`} className="sr-only">Email address</label>
        <Input
          ref={inputRef}
          id={`${testidPrefix}-email`}
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="name@email.com"
          value={email}
          onChange={(e) => { setEmail(e.target.value); if (status === "error") setStatus("idle"); }}
          data-testid={`${testidPrefix}-email-input`}
          aria-invalid={status === "error"}
          aria-describedby={`${testidPrefix}-help`}
          className="h-12 flex-1 border-[#D8DDE4] bg-white text-base"
        />
        <button
          type="submit"
          disabled={status === "loading"}
          data-testid={`${testidPrefix}-submit-button`}
          className="inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-[#0B132B] px-6 font-heading text-base font-bold text-white transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-70"
        >
          {status === "loading" ? <Loader2 className="h-5 w-5 animate-spin" /> : "Send my free planner"}
        </button>
      </div>
      {status === "error" ? (
        <p className="mt-3 text-sm font-medium text-[#C0392B]" id={`${testidPrefix}-help`} role="alert" data-testid={`${testidPrefix}-error`}>{msg}</p>
      ) : (
        <p className="mt-3 text-sm text-[#5A6472]" id={`${testidPrefix}-help`}>Get the free daily planner by email. Unsubscribe anytime.</p>
      )}
    </form>
  );
};

/* ============ HERO ============ */
const Hero = () => {
  const openCheckout = useContext(CheckoutContext);
  return (
    <section className="bg-white px-6 pt-8 pb-14 md:pt-16 md:pb-24" data-testid="hero-section">
      <div className="mx-auto grid max-w-[1120px] items-center gap-9 md:grid-cols-2 md:gap-14">
        <div className="text-center md:text-left">
          <span className="inline-flex items-center rounded-full bg-[#15816B]/10 px-4 py-1.5 text-xs font-bold tracking-[0.18em] text-[#15816B]" data-testid="hero-eyebrow">
            PLAN · FOCUS · DO · THRIVE
          </span>
          <h1 className="mt-5 font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-[#0B132B] sm:text-5xl lg:text-6xl" data-testid="hero-h1">
            When your brain feels overwhelmed, start here.
          </h1>
          <p className="mx-auto mt-5 max-w-[560px] text-base leading-relaxed text-[#5A6472] md:mx-0 md:text-lg" data-testid="hero-sub">
            CommandCenterADHD is the ADHD planning system that helps you choose what matters, find your next step, focus, and restart when the day goes off track.
          </p>
          <div className="mt-7">
            <button
              type="button"
              onClick={openCheckout}
              data-testid="hero-cta-button"
              data-cta-location="hero"
              className="inline-flex w-full items-center justify-center rounded-xl bg-[#0B132B] px-7 py-4 font-heading text-base font-extrabold text-white transition-transform duration-200 hover:-translate-y-0.5 sm:w-auto"
            >
              Get CommandCenterADHD for ${PRICE}, one time
            </button>
            <p className="mt-3 text-sm text-[#8A94A6]" data-testid="hero-microline">
              One time purchase. No subscription. 30-day money-back guarantee.
            </p>
            <p className="mt-4 text-sm" data-testid="hero-secondary">
              <a href="/free-planner" data-testid="hero-free-link" className="font-semibold text-[#15816B] underline-offset-2 hover:underline">
                Or get the free printable planner
              </a>
            </p>
          </div>
        </div>
        <div data-testid="hero-visual">
          <img
            src="/proof-command-center.jpg"
            alt="The CommandCenterADHD Notion system with one priority, quick wins, and time blocks"
            width="1200"
            height="800"
            fetchPriority="high"
            style={{ aspectRatio: "3 / 2" }}
            className="w-full rounded-2xl border border-[#E6E9EE] shadow-[0_24px_60px_-24px_rgba(11,19,43,0.35)]"
          />
        </div>
      </div>
    </section>
  );
};

/* ============ AUTOPSY ============ */
const causes = [
  {
    tag: "Problem 1",
    icon: Skull,
    title: "Every task looks equally important",
    body: "When everything competes for attention, choosing where to start becomes another task.",
    fix: "Solution: One Priority",
    sol: "Choose the single task that matters most right now.",
  },
  {
    tag: "Problem 2",
    icon: BatteryLow,
    title: "Assumes your energy stays the same",
    body: "Attention and capacity change, but fixed schedules keep asking for the same output.",
    fix: "Solution: Energy Blocks",
    sol: "Choose tasks based on the capacity you have today instead of following a schedule that no longer fits.",
  },
  {
    tag: "Problem 3",
    icon: RotateCcw,
    title: "Treats a difficult day as failure",
    body: "Missed tasks can create guilt, avoidance, and a backlog that becomes harder to reopen.",
    fix: "Solution: Reset",
    sol: "Review what remains, keep what still matters, and choose where to begin again.",
  },
];

const VideoDemo = () => {
  const wrapRef = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            obs.disconnect();
          }
        });
      },
      { rootMargin: "250px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const goToPlanner = () => {
    const input = document.getElementById("hero-lead-email");
    if (input) {
      input.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => input.focus({ preventScroll: true }), 450);
    }
  };

  return (
    <section className="bg-white px-6 pb-20 pt-2 md:pb-24" data-testid="video-demo-section">
      <div className="mx-auto max-w-[1000px] text-center">
        <h2 className="font-heading text-3xl font-extrabold tracking-tight text-[#0B132B] md:text-4xl" data-testid="video-demo-heading">
          See CommandCenterADHD in Action
        </h2>
        <p className="mx-auto mt-3 max-w-[560px] text-base text-[#5A6472]">
          A quick look at how the daily planner and the full Notion system work together.
        </p>
        <div
          ref={wrapRef}
          className="relative mt-8 overflow-hidden rounded-2xl border border-[#E6E9EE] bg-[#0B132B] shadow-[0_24px_60px_-24px_rgba(11,19,43,0.45)]"
          style={{ aspectRatio: "16 / 9" }}
          data-testid="video-demo-frame"
        >
          {inView ? (
            <video
              className="h-full w-full object-contain"
              src="/cc-demo.mp4"
              poster="/cc-demo-poster.jpg"
              controls
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              data-testid="video-demo-player"
              aria-label="CommandCenterADHD product demo video"
            >
              Your browser does not support the video tag.
            </video>
          ) : (
            <img
              src="/cc-demo-poster.jpg"
              alt="CommandCenterADHD product demo preview"
              className="h-full w-full object-cover"
              loading="lazy"
              data-testid="video-demo-poster"
            />
          )}
        </div>
        <button
          type="button"
          onClick={goToPlanner}
          data-testid="video-demo-cta-button"
          className="mt-8 inline-flex items-center justify-center rounded-xl bg-[#15816B] px-7 py-3.5 font-heading text-base font-bold text-white transition-transform duration-200 hover:-translate-y-0.5"
        >
          Get the Free Daily Planner
        </button>
      </div>
    </section>
  );
};

const PROOF_SHOTS = [
  { img: "/proof-command-center.jpg", label: "Your daily command center", caption: "Start, review, and reset from one page, with task views by day, priority, and energy." },
  { img: "/proof-brain-dump.jpg", label: "Brain Dump", caption: "Capture whatever is on your mind first, then decide what actually needs action." },
  { img: "/proof-reset.jpg", label: "The daily reset", caption: "Built to expect a hard day. Reflect, let go, and surface a low-effort next step." },
  { img: "/proof-projects.jpg", label: "Projects, connected", caption: "Tasks roll up into projects with a simple To do / Doing / Done board." },
];

const ProductProof = () => (
  <section className="bg-[#F7F5F0] px-6 py-20 md:py-24" data-testid="product-proof-section">
    <div className="mx-auto max-w-[1100px]">
      <div className="text-center">
        <h2 className="font-heading text-3xl font-extrabold tracking-tight text-[#0B132B] md:text-4xl" data-testid="product-proof-heading">
          A real look inside the Command Center
        </h2>
        <p className="mx-auto mt-3 max-w-[560px] text-base text-[#5A6472]">
          Actual screenshots from the Notion system, not mockups.
        </p>
      </div>
      <div className="mt-10 grid gap-6 md:grid-cols-2">
        {PROOF_SHOTS.map((s, i) => (
          <figure
            key={s.img}
            className="overflow-hidden rounded-2xl border border-[#E6E9EE] bg-white shadow-[0_16px_40px_-24px_rgba(11,19,43,0.35)]"
            data-testid={`product-proof-card-${i}`}
          >
            <div className="overflow-hidden border-b border-[#EEF1F5] bg-[#0B132B]" style={{ aspectRatio: "16 / 9" }}>
              <img
                src={s.img}
                alt={`CommandCenterADHD: ${s.label}`}
                loading="lazy"
                className="h-full w-full object-cover object-top"
                data-testid={`product-proof-image-${i}`}
              />
            </div>
            <figcaption className="p-5">
              <p className="font-heading text-lg font-bold text-[#0B132B]">{s.label}</p>
              <p className="mt-1.5 text-sm leading-relaxed text-[#5A6472]">{s.caption}</p>
            </figcaption>
          </figure>
        ))}
      </div>
    </div>
  </section>
);

const Autopsy = () => (
  <section id="autopsy" className="scroll-mt-20 bg-[#F6F8FA] px-6 py-20 md:py-24" data-testid="autopsy-section">
    <div className="mx-auto max-w-[1080px]">
      <div className="mx-auto max-w-[620px] text-center">
        <h2 className="font-heading text-3xl font-extrabold tracking-tight text-[#0B132B] md:text-4xl">
          The planner autopsy
        </h2>
        <p className="mt-4 text-base leading-relaxed text-[#5A6472] md:text-lg">
          Traditional planners often assume clear priorities, steady energy, and
          perfect follow through. CommandCenterADHD handles those three moments
          differently.
        </p>
      </div>
      <div className="mt-12 grid gap-6 md:grid-cols-3">
        {causes.map((c, i) => (
          <div
            key={i}
            data-testid={`cause-card-${i}`}
            className="flex flex-col rounded-2xl border border-[#E6E9EE] bg-white p-6 shadow-[0_1px_2px_rgba(11,19,43,0.04)]"
          >
            <span className="inline-flex w-fit items-center gap-2 rounded-full bg-[#FBE9E8] px-3 py-1 text-xs font-bold uppercase tracking-wide text-[#B4443E]">
              <c.icon className="h-3.5 w-3.5" /> {c.tag}
            </span>
            <p className="mt-4 font-heading text-lg font-extrabold text-[#0B132B]">{c.title}</p>
            <p className="mt-2 flex-1 text-[15px] leading-relaxed text-[#5A6472]">{c.body}</p>
            <span className="mt-5 inline-flex w-fit items-center gap-1.5 rounded-full bg-[#15816B]/10 px-3 py-1.5 text-sm font-semibold text-[#15816B]">
              <Check className="h-4 w-4" /> {c.fix}
            </span>
            <p className="mt-3 text-[14px] leading-relaxed text-[#5A6472]">{c.sol}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ============ ONE PAGE PREVIEW ============ */
const features = [
  "One Priority for choosing what matters now",
  "A place to capture tasks before they are forgotten",
  "Today, Upcoming, Needs Review, Effort, and All Tasks views",
  "Daily and weekly journals",
  "Project tracking for larger responsibilities",
  "Reset actions for restarting the day and reviewing the week",
  "Five setup videos",
];

const OnePage = () => (
  <section className="bg-white px-6 py-20 md:py-24" data-testid="onepage-section">
    <div className="mx-auto grid max-w-[1000px] items-center gap-12 md:grid-cols-2">
      <div className="order-2 flex justify-center md:order-1">
        <div className="w-full max-w-[360px] overflow-hidden rounded-2xl border border-[#E6E9EE] bg-[#F6F8FA] shadow-[0_20px_50px_-20px_rgba(11,19,43,0.35)]" data-testid="planner-preview">
          <img src={COVER_URL} alt="The Command Center daily planner cover" width="760" height="983" className="h-auto w-full object-cover" />
        </div>
      </div>
      <div className="order-1 md:order-2">
        <h2 className="font-heading text-3xl font-extrabold leading-tight tracking-tight text-[#0B132B] md:text-4xl">
          A clear view for today. Everything else stays organized.
        </h2>
        <p className="mt-5 text-base leading-relaxed text-[#5A6472] md:text-lg">
          The free PDF gives you a focused page for today. The Notion Command
          Center adds task capture, focused task views, daily and weekly
          journals, projects, and reset actions. Start with today and open the
          rest only when you need it.
        </p>
        <ul className="mt-7 space-y-3">
          {features.map((f, i) => (
            <li key={i} className="flex items-start gap-3" data-testid={`feature-${i}`}>
              <span className="mt-0.5 inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#15816B]/10 text-[#15816B]">
                <Check className="h-4 w-4" />
              </span>
              <span className="text-[15px] leading-relaxed text-[#2B3444]">{f}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

/* ============ TESTIMONIALS ============ */
const TESTIMONIALS = [
  {
    name: "Jackie J.",
    tag: "New customer",
    quote:
      "Before CommandCenterADHD, my day felt like a long list of things I meant to do but could not organize. Having one calm place to see my priorities helped me start with the next task instead of trying to solve everything at once.",
  },
  {
    name: "Delores A.",
    tag: "Early user",
    quote:
      "CommandCenterADHD did not make me feel judged for falling behind. Breaking tasks into smaller steps and seeing a simple plan for the day made it easier for me to reset and keep moving.",
  },
  {
    name: "Michael B.",
    tag: "Early user",
    quote:
      "I used to switch between notes, reminders, and messages without knowing what mattered first. CommandCenterADHD gave me a clearer view of my commitments, so I could choose one realistic next action.",
  },
];

const Testimonials = () => (
  <section className="bg-white px-6 py-20 md:py-24" data-testid="testimonials-section">
    <div className="mx-auto max-w-[1100px]">
      <div className="text-center">
        <h2 className="font-heading text-3xl font-extrabold tracking-tight text-[#0B132B] md:text-4xl" data-testid="testimonials-heading">
          People who kept starting over, and finally stopped
        </h2>
        <p className="mx-auto mt-3 font-heading text-lg font-bold text-[#15816B] md:text-xl" data-testid="testimonials-subheading">
          And the people around them noticed.
        </p>
        <p className="mx-auto mt-2 max-w-[560px] text-base text-[#5A6472]">
          Real words from Command Center owners, added as they come in.
        </p>
      </div>
      <div className="mt-12 grid gap-6 md:grid-cols-3">
        {TESTIMONIALS.map((t, i) => (
          <figure
            key={t.name}
            className="flex h-full flex-col rounded-2xl border border-[#E6E9EE] bg-[#F7F5F0] p-7"
            data-testid={`testimonial-card-${i}`}
          >
            <Quote className="h-7 w-7 text-[#15816B]" aria-hidden="true" />
            <blockquote
              className="mt-4 flex-1 font-heading text-lg font-bold leading-snug text-[#0B132B]"
              data-testid={`testimonial-quote-${i}`}
            >
              {t.quote}
            </blockquote>
            <figcaption className="mt-6">
              <p className="font-heading text-base font-extrabold text-[#0B132B]" data-testid={`testimonial-name-${i}`}>
                {t.name}
              </p>
              <p className="mt-1 text-sm text-[#5A6472]" data-testid={`testimonial-tag-${i}`}>
                {t.tag}
              </p>
            </figcaption>
          </figure>
        ))}
      </div>
    </div>
  </section>
);

/* ============ COMMUNITY PREVIEW ============ */
// eslint-disable-next-line no-unused-vars
const CommunityPreview = () => (
  <section className="bg-[#F6F8FA] px-6 pt-20 md:pt-24" data-testid="community-preview-section">
    <div className="mx-auto max-w-[1000px]">
      <div className="text-center">
        <h2 className="font-heading text-3xl font-extrabold tracking-tight text-[#0B132B] md:text-4xl" data-testid="community-preview-heading">
          A calmer place to get unstuck
        </h2>
      </div>
      <div className="mt-10 overflow-hidden rounded-2xl border border-[#E6E9EE] bg-white shadow-[0_16px_40px_-24px_rgba(11,19,43,0.35)]">
        <div className="flex items-center gap-2 border-b border-[#EEF1F5] bg-[#F7F9FA] px-4 py-3">
          <span className="h-3 w-3 rounded-full bg-[#E6E9EE]" />
          <span className="h-3 w-3 rounded-full bg-[#E6E9EE]" />
          <span className="h-3 w-3 rounded-full bg-[#E6E9EE]" />
          <span className="ml-3 truncate text-xs text-[#8A94A6]">commandcenteradhd.com/community</span>
        </div>
        <div className="relative overflow-hidden bg-[#FAF9F5]" style={{ height: "460px" }}>
          <iframe
            src="/community-preview.html"
            title="Preview of the Command Center community feed"
            aria-hidden="true"
            tabIndex={-1}
            sandbox="allow-scripts"
            loading="lazy"
            className="pointer-events-none block h-[900px] w-full border-0"
            data-testid="community-preview-frame"
          />
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white to-transparent"
            aria-hidden="true"
          />
        </div>
      </div>
      <p
        className="mx-auto mt-5 max-w-[720px] text-center text-[15px] leading-relaxed text-[#5A6472]"
        data-testid="community-preview-caption"
      >
        Community access included with your purchase: weekly resets, AMAs, and a feed built without likes or streaks.
      </p>
    </div>
  </section>
);

/* ============ PRICING ============ */
const Pricing = () => {
  const openCheckout = useContext(CheckoutContext);
  const focusFree = useContext(FreeFocusContext);
  const goFree = () => {
    scrollTo("hero-section");
    setTimeout(() => focusFree?.current?.focus(), 500);
  };
  return (
    <section id="pricing" className="scroll-mt-20 bg-[#F6F8FA] px-6 py-20 md:py-24" data-testid="pricing-section">
      <div className="mx-auto max-w-[900px]">
        <h2 className="text-center font-heading text-3xl font-extrabold tracking-tight text-[#0B132B] md:text-4xl">
          Start with the free planner. Add the full workspace when you need more.
        </h2>
        <div className="mt-12 grid items-start gap-6 md:grid-cols-2">
          {/* Free */}
          <div className="rounded-2xl border border-[#E6E9EE] bg-white p-7" data-testid="plan-free">
            <div className="mb-6 h-6 w-6 rounded-md border-2 border-[#D8DDE4]" />
            <p className="font-heading text-xl font-extrabold text-[#0B132B]">Daily planner PDF</p>
            <p className="mt-2 font-heading text-3xl font-extrabold text-[#15816B]">Free</p>
            <p className="mt-4 text-[15px] leading-relaxed text-[#5A6472]">
              A focused daily page for choosing one priority, planning realistic
              time blocks, and deciding what can wait.
            </p>
            <button
              type="button"
              onClick={goFree}
              data-testid="free-download-button"
              className="mt-6 w-full rounded-xl border border-[#0B132B] py-3 font-heading text-base font-bold text-[#0B132B] transition-colors hover:bg-[#0B132B] hover:text-white"
            >
              Get the free planner
            </button>
          </div>
          {/* Full system */}
          <div className="relative rounded-2xl border-2 border-[#15816B] bg-white p-7 shadow-[0_20px_50px_-24px_rgba(21,129,107,0.5)]" data-testid="plan-paid">
            <span className="absolute -top-3 left-7 rounded-full bg-[#15816B] px-3 py-1 font-heading text-[11px] font-extrabold uppercase tracking-wide text-white" data-testid="plan-paid-badge">
              Recommended
            </span>
            <div className="mb-6 mt-2 h-6 w-6 rounded-md border-2 border-[#15816B]" />
            <p className="font-heading text-xl font-extrabold text-[#0B132B]">Notion Command Center</p>
            <p className="mt-2">
              <span className="font-heading text-3xl font-extrabold text-[#0B132B]">${PRICE}</span>
              <span className="ml-2 text-[15px] text-[#5A6472]">one time purchase, future updates included</span>
            </p>
            <p className="mt-2 text-[13px] font-medium text-[#5A6472]" data-testid="plan-paid-value-line">
              Less than one month of a subscription planner app. Yours forever.
            </p>
            <p className="mt-3 flex items-start gap-2 text-[13px] leading-relaxed text-[#15816B]" data-testid="refund-line">
              <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
              <span>
                30-day money-back guarantee. Email support@commandcenteradhd.com and we&apos;ll refund you, no forms, no questions.
              </span>
            </p>
            <p className="mt-3 text-sm font-semibold text-[#15816B]">Community access included for Command Center owners.</p>
            <p className="mt-4 text-[15px] leading-relaxed text-[#5A6472]">
              Includes task capture, focused task views, daily and weekly
              journals, project tracking, reset actions, and setup videos.
            </p>
            <button
              type="button"
              onClick={openCheckout}
              data-testid="pricing-cta-button"
              data-cta-location="pricing"
              className="mt-6 w-full rounded-xl bg-[#0B132B] py-3 font-heading text-base font-bold text-white transition-transform duration-200 hover:-translate-y-0.5"
            >
              Get the Command Center for ${PRICE}
            </button>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-3 border-t border-[#EEF1F5] pt-5" data-testid="trust-badges">
              <img
                src="https://www.paypalobjects.com/webstatic/en_US/i/buttons/pp-acceptance-medium.png"
                alt="PayPal Acceptance Mark"
                width="150"
                height="40"
                loading="lazy"
                className="h-6 w-auto"
                data-testid="paypal-badge"
              />
              <span className="flex items-center gap-1.5 text-[13px] font-medium text-[#5A6472]" data-testid="secure-checkout-badge">
                <Lock className="h-4 w-4 text-[#15816B]" aria-hidden="true" />
                Secure checkout
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============ COMMUNITY ============ */
// eslint-disable-next-line no-unused-vars
const Community = () => (
  <section className="bg-white px-6 py-14" data-testid="community-section">
    <div className="mx-auto flex max-w-[1000px] flex-col items-start justify-between gap-6 rounded-3xl bg-[#0B132B] p-8 md:flex-row md:items-center md:p-10">
      <div className="max-w-[560px]">
        <h2 className="flex items-center gap-3 font-heading text-2xl font-extrabold text-white">
          <Users className="h-6 w-6 text-[#4FD1B5]" /> Get help when you need it
        </h2>
        <p className="mt-3 text-[15px] leading-relaxed text-[#AFC0D4]">
          Command Center owners can ask questions, join scheduled AMA sessions,
          take part in the weekly reset, and use the setup videos whenever they
          need a refresher.
        </p>
      </div>
      <span className="inline-flex flex-shrink-0 items-center gap-2 rounded-xl bg-[#15816B] px-5 py-3 font-heading text-sm font-bold text-white">
        <Check className="h-4 w-4" /> Included for Command Center owners
      </span>
    </div>
  </section>
);

/* ============ FAQ ============ */
const faqs = [
  {
    q: "Who is this for?",
    a: "Anyone whose planning falls apart on hard days. If long to-do lists leave you stuck, CommandCenterADHD helps you narrow to one priority, find a next step, and restart without rebuilding everything.",
  },
  {
    q: "Is this medical treatment?",
    a: "No, it is a planning system. CommandCenterADHD does not diagnose or treat anything. It helps keep tasks visible, narrow your attention to one priority, and restart your plan after a difficult day.",
  },
  {
    q: "Is it a one time purchase?",
    a: "Yes. You pay $17 once. There is no subscription and no recurring charge, and future updates are included.",
  },
  {
    q: "What do I need to use it?",
    a: "Works with the free Notion plan in any browser. You do not need a paid Notion account or any extra software.",
  },
  {
    q: "What if I abandon it like everything else?",
    a: "Open the Reset view. Review what remains, keep what still matters, and choose the next task. You do not need to backfill missed days or rebuild the workspace.",
  },
  {
    q: "Do I need to know Notion?",
    a: "No. Duplicate the template and follow the setup videos when you need guidance. The main actions and task views are already organized for you.",
  },
  {
    q: "What is the difference between the free PDF and the paid system?",
    a: "The free PDF provides a focused daily planning page. The Notion Command Center adds task views, daily and weekly journals, projects, reset actions, setup videos, and community access.",
  },
  {
    q: "How do I get my planner after I enter my email?",
    a: "The planner opens in your browser after you submit your email. We also send the link to your inbox so you can return to it later.",
  },
  {
    q: "What is your refund policy?",
    a: "Try it for 30 days. If it does not fit your routine, request a full refund by emailing support@commandcenteradhd.com from the address you purchased with.",
  },
];

const Faq = () => (
  <section id="faq" className="scroll-mt-20 bg-[#F6F8FA] px-6 py-20 md:py-24" data-testid="faq-section">
    <div className="mx-auto max-w-[720px]">
      <h2 className="border-b border-[#DCE1E8] pb-5 font-heading text-3xl font-extrabold tracking-tight text-[#0B132B] md:text-4xl">
        Honest answers
      </h2>
      <Accordion type="single" collapsible className="mt-4 divide-y divide-[#DCE1E8]">
        {faqs.map((o, i) => (
          <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`} className="border-none">
            <AccordionTrigger className="py-5 text-left font-heading text-base font-bold text-[#0B132B] hover:no-underline">
              {o.q}
            </AccordionTrigger>
            <AccordionContent className="pb-5 text-[15px] leading-relaxed text-[#5A6472]">
              {o.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

/* ============ HOW IT WORKS ============ */
const steps = [
  { k: "Plan", t: "Choose the one thing that matters most today, so you are not carrying the whole list at once." },
  { k: "Focus", t: "Line up your day around the energy you actually have, not the energy you wish you had." },
  { k: "Do", t: "Take the next small step, check it off, and let a little momentum build." },
  { k: "Thrive", t: "When the day goes sideways, reset in one tap and keep what still matters." },
];
const HowItWorks = () => (
  <section className="bg-white px-6 py-20 md:py-24" data-testid="how-it-works-section">
    <div className="mx-auto max-w-[1000px]">
      <h2 className="text-center font-heading text-3xl font-extrabold tracking-tight text-[#0B132B] md:text-4xl" data-testid="how-it-works-heading">
        How it works
      </h2>
      <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {steps.map((s, i) => (
          <div key={s.k} className="rounded-2xl border border-[#E6E9EE] bg-[#F9FAFB] p-6" data-testid={`step-card-${i}`}>
            <span className="font-heading text-sm font-bold text-[#15816B]">{`0${i + 1}`}</span>
            <p className="mt-2 font-heading text-lg font-extrabold text-[#0B132B]">{s.k}</p>
            <p className="mt-2 text-sm leading-relaxed text-[#5A6472]">{s.t}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ============ LOW BATTERY MODE (teaser) ============ */
const LowBatteryTeaser = () => (
  <section className="bg-[#0B132B] px-6 py-20 md:py-24" data-testid="low-battery-section">
    <div className="mx-auto grid max-w-[1000px] items-center gap-10 md:grid-cols-2">
      <div>
        <span className="inline-flex items-center rounded-full bg-white/10 px-4 py-1.5 text-xs font-bold tracking-[0.16em] text-[#7FE0C6]">
          LOW BATTERY MODE
        </span>
        <h2 className="mt-5 font-heading text-3xl font-extrabold tracking-tight text-white md:text-4xl" data-testid="low-battery-heading">
          For the days you have almost nothing left
        </h2>
        <p className="mt-4 text-base leading-relaxed text-[#BFD4E8]">
          When you feel overwhelmed, the plan gets smaller, not louder. The whole list steps back and you see one
          must do, one quick win, and a short focus timer. Forget the rest for a moment and get one thing moving.
        </p>
      </div>
      <div
        className="rounded-2xl border border-white/10 bg-[#14213D] p-6 text-white"
        data-testid="low-battery-visual"
        aria-label="Preview of the simplified Overwhelmed view"
      >
        <p className="text-xs font-semibold uppercase tracking-wide text-[#7FE0C6]">Must do</p>
        <p className="mt-1 text-lg font-bold">Reply to the one email that is weighing on you</p>
        <p className="mt-5 text-xs font-semibold uppercase tracking-wide text-[#7FE0C6]">One quick win</p>
        <p className="mt-1 text-base text-[#DCE6F2]">Refill your water bottle</p>
        <div className="mt-5 flex items-center gap-3 rounded-xl bg-white/5 px-4 py-3">
          <span className="font-heading text-2xl font-extrabold">05:00</span>
          <span className="text-sm text-[#9FB2C9]">gentle focus timer</span>
        </div>
      </div>
    </div>
  </section>
);

/* ============ WHAT'S INCLUDED ============ */
const included = [
  "The full CommandCenterADHD Notion system, ready to duplicate",
  "Daily and weekly journals to plan and look back",
  "Task and project views that keep everything visible",
  "Reset actions to restart after an off day",
  "Setup videos that walk you through it at your pace",
  "Community access with other members",
  "Future updates included, at no extra cost",
];
const WhatsIncluded = () => (
  <section className="bg-white px-6 py-20 md:py-24" data-testid="whats-included-section">
    <div className="mx-auto max-w-[820px]">
      <h2 className="text-center font-heading text-3xl font-extrabold tracking-tight text-[#0B132B] md:text-4xl" data-testid="whats-included-heading">
        What your $17 includes
      </h2>
      <ul className="mx-auto mt-10 grid max-w-[720px] gap-3 sm:grid-cols-2">
        {included.map((item, i) => (
          <li key={i} className="flex items-start gap-3 rounded-xl border border-[#E6E9EE] bg-[#F9FAFB] p-4" data-testid={`included-item-${i}`}>
            <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#15816B]" aria-hidden="true" />
            <span className="text-sm leading-relaxed text-[#0B132B]">{item}</span>
          </li>
        ))}
      </ul>
      <p className="mx-auto mt-8 max-w-[620px] text-center text-sm leading-relaxed text-[#5A6472]" data-testid="whats-included-note">
        Access is delivered by email as a link right after checkout. Works with the free Notion plan in any browser.
      </p>
    </div>
  </section>
);

/* ============ FINAL CTA ============ */
const FinalCta = () => {
  const openCheckout = useContext(CheckoutContext);
  return (
    <section className="bg-[#F6F8FA] px-6 py-20 md:py-24" data-testid="final-cta-section">
      <div className="mx-auto max-w-[720px] text-center">
        <h2 className="font-heading text-3xl font-extrabold tracking-tight text-[#0B132B] md:text-4xl" data-testid="final-cta-heading">
          Choose what matters, focus, and restart when you need to.
        </h2>
        <p className="mx-auto mt-4 max-w-[520px] text-base text-[#5A6472]">
          One calm planning system for the way your day actually goes.
        </p>
        <button
          type="button"
          onClick={openCheckout}
          data-testid="final-cta-button"
          data-cta-location="final"
          className="mt-8 inline-flex w-full items-center justify-center rounded-xl bg-[#0B132B] px-7 py-4 font-heading text-base font-extrabold text-white transition-transform duration-200 hover:-translate-y-0.5 sm:w-auto"
        >
          Get CommandCenterADHD for ${PRICE}, one time
        </button>
        <p className="mt-3 text-sm text-[#8A94A6]">One time purchase. No subscription. 30-day money-back guarantee.</p>
      </div>
    </section>
  );
};

/* ============ PAYPAL ============ */
const PaypalPay = ({ emailRef, setError, onPaid }) => {
  const [config, setConfig] = useState(null);
  const validationErrorRef = useRef(false);
  useEffect(() => {
    getPaypalConfig().then(setConfig).catch(() => setConfig(null));
  }, []);

  if (!config || !config.client_id) return null;
  const emailValid = () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailRef.current || "");

  return (
    <div data-testid="paypal-button-wrap">
      <PayPalScriptProvider options={{ "client-id": config.client_id, currency: config.currency || "USD", intent: "capture" }}>
        <PayPalButtons
          style={{ layout: "horizontal", color: "gold", shape: "pill", label: "paypal", tagline: false, height: 48 }}
          createOrder={async () => {
            if (!emailValid()) {
              setError("Please enter a valid email address first.");
              validationErrorRef.current = true;
              throw new Error("invalid_email");
            }
            setError("");
            validationErrorRef.current = false;
            const orderId = await createPaypalOrder(emailRef.current);
            trackBeginCheckout({ method: "paypal" });
            return orderId;
          }}
          onApprove={async (data) => {
            const result = await capturePaypalOrder(data.orderID);
            if (result.access_token) {
              const params = new URLSearchParams({
                token: result.access_token,
                email: result.email || emailRef.current || "",
                slug: result.product_slug || "",
                amount: String(result.amount_total || 1700),
              });
              onPaid(`/thank-you?${params.toString()}`);
            } else {
              setError("Payment captured but no access token returned. Check your email for the link.");
            }
          }}
          onError={() => {
            if (validationErrorRef.current) {
              validationErrorRef.current = false;
              return;
            }
            setError("Something went wrong with PayPal. Please try again or use card.");
          }}
        />
      </PayPalScriptProvider>
    </div>
  );
};

/* ============ CHECKOUT DIALOG ($17 paid) ============ */
const CheckoutDialog = ({ open, onOpenChange }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const emailRef = useRef("");
  emailRef.current = email;

  const submit = async (e) => {
    e.preventDefault();
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!valid) {
      setError("Please enter a valid email address.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await initiateCheckout(email);
    } catch (err) {
      setLoading(false);
      setError("Something went wrong starting checkout. Please try again.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]" data-testid="checkout-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-extrabold text-[#0B132B]">
            Get the Notion Command Center
          </DialogTitle>
          <DialogDescription className="text-[#5A5A5A]">
            Enter your email. Your access link is delivered here instantly after
            payment. No account required.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} noValidate className="mt-2 space-y-4">
          <label htmlFor="checkout-email" className="sr-only">Email address</label>
          <Input
            id="checkout-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoFocus
            placeholder="name@email.com"
            value={email}
            onChange={(e) => { setEmail(e.target.value); if (error) setError(""); }}
            data-testid="checkout-email-input"
            aria-invalid={!!error}
            aria-describedby={error ? "checkout-email-error" : undefined}
            className="h-12 text-base"
          />
          {error && (
            <p className="text-sm font-medium text-[#C0392B]" id="checkout-email-error" role="alert" data-testid="checkout-email-error">{error}</p>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="checkout-continue-button"
            className="group inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#0B132B] py-4 font-heading text-lg font-extrabold text-white transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-70"
          >
            {loading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" /> Redirecting to secure checkout
              </>
            ) : (
              <>
                Pay by card <ArrowRight className="h-5 w-5" />
              </>
            )}
          </button>
        </form>
        <div className="my-3 flex items-center gap-3" data-testid="checkout-divider">
          <span className="h-px flex-1 bg-[#E2E2E2]" />
          <span className="text-xs font-semibold uppercase tracking-wide text-[#9AA5B1]">or</span>
          <span className="h-px flex-1 bg-[#E2E2E2]" />
        </div>
        <PaypalPay emailRef={emailRef} setError={setError} onPaid={(url) => { window.location.href = url; }} />
        <p className="mt-3 text-center text-xs text-[#7BA8C4]">
          Secure payment by Stripe or PayPal. ${PRICE} one time purchase, future updates included, no subscription. Instant digital
          delivery. Try it for 30 days. If it does not fit your routine, request
          a full refund.
        </p>
      </DialogContent>
    </Dialog>
  );
};

/* ============ RESEND DIALOG ============ */
const ResendDialog = ({ open, onOpenChange }) => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/support/resend-access`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
    } catch (err) {
      /* generic response regardless */
    }
    setStatus("done");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]" data-testid="resend-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-extrabold text-[#0B132B]">
            Lost your access link?
          </DialogTitle>
          <DialogDescription className="text-[#5A5A5A]">
            Enter the email you purchased with and we will resend your access link.
          </DialogDescription>
        </DialogHeader>
        {status === "done" ? (
          <p className="mt-2 text-[15px] text-[#0B132B]" data-testid="resend-done">
            If an order exists for that email, your access instructions are on the way.
          </p>
        ) : (
          <form onSubmit={submit} className="mt-2 space-y-4">
            <Input
              type="email"
              autoFocus
              placeholder="name@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="resend-email-input"
              className="h-12 text-base"
            />
            <button
              type="submit"
              disabled={status === "loading"}
              data-testid="resend-submit-button"
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#0B132B] py-3.5 font-heading text-base font-bold text-white transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-70"
            >
              {status === "loading" ? <Loader2 className="h-5 w-5 animate-spin" /> : "Resend my access link"}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

/* ============ EXIT INTENT (A/B) ============ */
const EXIT_VARIANTS = {
  A: {
    title: "Before you go, take the free planner",
    body: "One page, printable tonight. No account, no spam. See if the system that expects a bad day actually sticks for you.",
  },
  B: {
    title: "Wait, don't leave without the free planner",
    body: "The free daily planner PDF is one page and printable tonight. No account, no spam. Try the system that expects a bad day.",
  },
};

const ExitIntentDialog = ({ open, onOpenChange, variant = "A" }) => {
  const copy = EXIT_VARIANTS[variant] || EXIT_VARIANTS.A;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]" data-testid="exit-intent-dialog" data-variant={variant}>
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-extrabold text-[#0B132B]">
            {copy.title}
          </DialogTitle>
          <DialogDescription className="text-[#5A5A5A]">
            {copy.body}
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2">
          <FreeCapture testidPrefix="exit-lead" variant={variant} source="exit_popup" />
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ============ PAGE ============ */
export default function Landing() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resendOpen, setResendOpen] = useState(false);
  const [exitOpen, setExitOpen] = useState(false);
  const [exitVariant, setExitVariant] = useState("A");
  const freeInputRef = useRef(null);

  useEffect(() => {
    persistUtm();
    trackEvent("page_view", { page: "storefront" });
    trackHomepageView();
    initCtaTracking();
    // Fire view_pricing once when the pricing section scrolls into view.
    const pricingEl = document.querySelector('[data-testid="pricing-section"]');
    let observer;
    if (pricingEl && "IntersectionObserver" in window) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((en) => {
            if (en.isIntersecting) {
              trackPricingView();
              observer.disconnect();
            }
          });
        },
        { threshold: 0.4 }
      );
      observer.observe(pricingEl);
    }
    if (window.location.hash) {
      const id = window.location.hash.slice(1);
      setTimeout(() => scrollTo(id), 300);
    }
    return () => {
      if (observer) observer.disconnect();
    };
  }, []);

  useEffect(() => {
    let armed = false;
    const arm = setTimeout(() => { armed = true; }, 6000);
    const onMouseOut = (e) => {
      if (!armed || e.clientY > 0 || e.relatedTarget) return;
      let seen = false;
      try {
        seen = sessionStorage.getItem("cc_exit_shown") || localStorage.getItem("cc_lead_captured");
      } catch (err) { /* no-op */ }
      if (seen) return;
      let variant = "A";
      try {
        variant = localStorage.getItem("cc_exit_variant") || (Math.random() < 0.5 ? "A" : "B");
        localStorage.setItem("cc_exit_variant", variant);
        sessionStorage.setItem("cc_exit_shown", "1");
      } catch (err) { /* no-op */ }
      setExitVariant(variant);
      abImpression(variant, "exit_popup");
      trackEvent("exit_intent_shown", { variant });
      setExitOpen(true);
    };
    document.addEventListener("mouseout", onMouseOut);
    return () => {
      clearTimeout(arm);
      document.removeEventListener("mouseout", onMouseOut);
    };
  }, []);

  const openCheckout = () => setDialogOpen(true);

  return (
    <CheckoutContext.Provider value={openCheckout}>
      <FreeFocusContext.Provider value={freeInputRef}>
        <LaunchBanner />
        <Header />
        <main className="font-body text-[#0F172A]" data-testid="landing-page">
          <Hero />
          <Autopsy />
          <HowItWorks />
          <LowBatteryTeaser />
          <VideoDemo />
          <ProductProof />
          <OnePage />
          <WhatsIncluded />
          <Testimonials />
          <Pricing />
          <Faq />
          <FinalCta />
        </main>
        <SiteFooter />
        <CheckoutDialog open={dialogOpen} onOpenChange={setDialogOpen} />
        <ResendDialog open={resendOpen} onOpenChange={setResendOpen} />
        <ExitIntentDialog open={exitOpen} onOpenChange={setExitOpen} variant={exitVariant} />
      </FreeFocusContext.Provider>
    </CheckoutContext.Provider>
  );
}
